import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Box, Button, Flex, Spacer, Textarea } from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Participants } from '../common/chat/participants';
import { TwilioRoomEvents } from '../../twilio/constants';
import { isInterpreterUser } from '../../utils/user/user';
import ChatTabs from '../common/chat/tabs';
import { LocalDataTrack } from 'twilio-video';
import {
  addChatMessage,
  addStatusMessage,
  getSettingsForChat,
  setChatHidden,
} from '../../redux/reducers';
import { useLocalDataTrack, useDataTrackMessages, useLocalParticipant } from '../../twilio/hooks';
import { MessagesIcon, NoMessagesIcon, SendIcon } from '../../images';

const chatTrackFilter = (track) => ['all', 'interpreters'].includes(track.name);
const interpretersOnlyTrackFilter = (track) => track.name === 'interpreters';

const Chat = ({ members, room, ...containerProps }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isChatHidden = useSelector((state) => state.activeSession.isChatHidden);
  const messages = useSelector((state) => state.activeSession.messages);
  const { color, bgColor, altBg, font, fontSize: fontSize2 } = useSelector(
    getSettingsForChat,
    shallowEqual
  );
  const [text, setText] = useState('');
  const [selectedChannel, setChannel] = useState('all');
  const { userData } = useSelector((state) => state.user);

  const roles = useSelector((state) => state.activeSession.roles);
  const self = members.find((m) => m.isSelf);
  const ownRole = self && roles[self.name];
  const isInterpreter = isInterpreterUser(userData) || ownRole === 'interpreter';
  const allInterpreters = members.filter((m) => roles[m.name] === 'interpreter');
  const numberOfInterpreters = allInterpreters.length;
  const tabs = useMemo(
    () =>
      isInterpreter && numberOfInterpreters > 1
        ? { all: t('chat.allTitle'), interpreters: t('chat.interpretersTitle') }
        : { all: t('chat.allTitle') },
    [isInterpreter, numberOfInterpreters]
  );
  if (!tabs[selectedChannel]) {
    setChannel('all');
  }

  const unseen = messages
    .filter((msg) => msg.isSeen === false)
    .reduce((prev, msg) => {
      if (!prev[msg.channel]) {
        return { ...prev, [msg.channel]: 1 };
      } else {
        return { ...prev, [msg.channel]: prev[msg.channel] + 1 };
      }
    }, {});
  const anyUnseen = Object.keys(unseen).length > 0;
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const messageHandler = useCallback(
    (message, track, participant) => {
      dispatch(addChatMessage(message, participant.identity, track.name));
    },
    [dispatch]
  );
  useDataTrackMessages(chatTrackFilter, messageHandler);

  const intTrack = useLocalDataTrack(interpretersOnlyTrackFilter);
  const localParticipant = useLocalParticipant();
  const connectionState = localParticipant && localParticipant.state;
  useEffect(() => {
    if (!localParticipant || connectionState != 'connected') {
      return;
    }
    if (isInterpreter) {
      if (!intTrack) {
        const interpretersOnlyTrack = new LocalDataTrack({
          name: `interpreters`,
          reliable: true,
        });
        try {
          localParticipant.publishTrack(interpretersOnlyTrack);
        } catch (err) {
          console.warn('Failed to publish int track', interpretersOnlyTrack, err);
        }
      } else {
        // might be already published, but just in case...
        try {
          localParticipant.publishTrack(intTrack);
        } catch (err) {
          console.warn('Failed to republish int track', intTrack, err);
        }
      }
    } else {
      if (intTrack) {
        localParticipant.unpublishTrack(intTrack);
      }
    }
  }, [isInterpreter, localParticipant, intTrack, connectionState]);

  useEffect(() => {
    if (!room) {
      return;
    }

    room.on(TwilioRoomEvents.ParticipantConnected, (participant) => {
      const name = participant.identity || t('chat.anonymous');
      dispatch(addStatusMessage('chat.joined', { name }));
    });
    room.on(TwilioRoomEvents.ParticipantDisconnected, (participant) => {
      const name = participant.identity || t('chat.anonymous');
      dispatch(addStatusMessage('chat.left', { name }));
    });
  }, [room && room.sid]);

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const chatTrack = useLocalDataTrack((track) => track.name === selectedChannel);
  const sendMessage = useCallback(
    (message) => {
      if (chatTrack) {
        chatTrack.send(message);
        dispatch(addChatMessage(message, null, selectedChannel));
        setText('');
      } else {
        console.error('cant send, data track not present', selectedChannel);
      }
    },
    [chatTrack]
  );

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        sendMessage(e.target.value);
        e.preventDefault();
      }
    },
    [sendMessage]
  );

  const openChat = useCallback(() => {
    dispatch(setChatHidden(false));
  }, []);

  const maxFontSize = 30;
  const fontSize = Number.parseInt(fontSize2) > maxFontSize ? '30pt' : fontSize2;

  if (isChatHidden) {
    return (
      <Flex width="48px" direction="column" alignItems="center" height="100%" bg="#eeeeee">
        <Box color="black">{anyUnseen ? <MessagesIcon /> : <NoMessagesIcon />}</Box>
        <Spacer />
        <Button
          bg="white"
          color="black"
          borderRadius="0"
          width="calc(100% - 8px)"
          p="0"
          ml="0"
          mr="8px"
          onClick={openChat}
        >
          <ChevronLeftIcon />
        </Button>
        <Spacer />
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      {...containerProps}
      sx={{ height: '100%', maxHeight: '100%', color, bg: bgColor }}
    >
      <ChatTabs tabs={tabs} select={setChannel} current={selectedChannel} unseen={unseen} />
      <Box
        sx={{
          flexShrink: 0,
          maxHeight: '30%',
          overflowY: 'scroll',
          fontFamily: font,
          fontSize,
          lineHeight: 'normal',
          borderBottom: '1px solid black',
          px: '2',
        }}
      >
        <Participants members={members} />
      </Box>

      <Flex
        sx={{
          overflowY: 'scroll',
          m: 2,
          flexDirection: 'column-reverse',
          fontFamily: font,
          fontSize,
          lineHeight: 'normal',
        }}
      >
        {/* column-reverse keeps it scrolled to bottom when new messages appear */}
        {messages
          .filter((m) => m.channel === selectedChannel)
          .reverse()
          .map((msg, ix) => {
            if (!msg.isSeen) {
              msg.isSeen = true;
              forceUpdate();
            }
            return msg.isNotification ? (
              <Box key={msg.id || ix}>{t(msg.i18nKey, msg.params)}</Box>
            ) : (
              <Box key={msg.id || ix} textAlign={msg.isSelf ? 'right' : 'left'} my={2}>
                <div>
                  {msg.isSelf ? '' : `[${msg.name}] `}
                  {msg.timestamp.format('D.M. HH:mm')}
                </div>
                <Flex justifyContent={msg.isSelf ? 'flex-end' : 'flex-start'}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 8,
                      bg: altBg,
                      maxWidth: '100%',
                      hyphens: 'auto',
                    }}
                  >
                    {msg.text}
                  </Box>
                </Flex>
              </Box>
            );
          })}
      </Flex>
      <Box flexGrow="1" />
      <Flex
        sx={{ flexShrink: 0, color: 'black', bg: 'yellow', py: 1, px: 2, alignItems: 'center' }}
      >
        <NoMessagesIcon />
        {selectedChannel === 'all' ? (
          <Trans i18nKey="chat.visibleToAll" />
        ) : (
          <Trans i18nKey="chat.visibleToInterpreters" />
        )}
      </Flex>
      <Box sx={{ flexShrink: 0, maxHeight: '25%', position: 'relative' }}>
        <Textarea
          sx={{
            fontFamily: font,
            fontSize,
            lineHeight: 'normal',
            minHeight: Math.min(240, 8 * Number.parseInt(fontSize)) + 'px',
          }}
          color={color}
          bg={bgColor}
          border="none"
          width="100%"
          value={text}
          onChange={handleTextChange}
          onKeyPress={handleKeyPress}
          placeholder={t('chat.writeMessagePlaceholder')}
        />
        <Button
          sx={{
            position: 'absolute',
            bottom: '4px',
            right: '4px',
            width: '48px',
            height: '48px',
            p: 0,
            background: 'none',
            zIndex: 1,
          }}
          onClick={() => sendMessage(text)}
        >
          <SendIcon />
        </Button>
      </Box>
    </Flex>
  );
};

Chat.propTypes = {
  members: PropTypes.array,
  room: PropTypes.object,
};

export default Chat;
